// src/pages/tickets/components/mobile/TicketCard.tsx
import { MessageSquare, Paperclip, Clock } from 'lucide-react';
import { cn, formatRelativeTime, formatDate } from '@/lib/utils';
import type { Ticket } from '@/types';

interface TicketCardProps {
  ticket: Ticket;
  onTap: (id: string) => void;
}

const STATUS_LABELS: Record<string, string> = {
  NUEVO: 'Nuevo',
  EN_ANALISIS: 'En Análisis',
  EN_DESARROLLO: 'En Desarrollo',
  EN_PRUEBAS: 'En Pruebas',
  RESUELTO: 'Resuelto',
  CERRADO: 'Cerrado',
  CANCELADO: 'Cancelado',
};

const STATUS_COLORS: Record<string, string> = {
  NUEVO: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  EN_ANALISIS: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  EN_DESARROLLO: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  EN_PRUEBAS: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  RESUELTO: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  CERRADO: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  CANCELADO: 'bg-gray-100 text-gray-500 dark:bg-gray-900 dark:text-gray-400',
};

const PRIORITY_COLORS: Record<string, string> = {
  CRITICA: 'text-red-600 dark:text-red-400',
  ALTA: 'text-orange-600 dark:text-orange-400',
  MEDIA: 'text-blue-600 dark:text-blue-400',
  BAJA: 'text-gray-500 dark:text-gray-400',
};

const PRIORITY_LABELS: Record<string, string> = {
  CRITICA: '!!',
  ALTA: '!',
  MEDIA: '',
  BAJA: '',
};

export function TicketCard({ ticket, onTap }: TicketCardProps) {
  const isClosed = ticket.estado === 'CERRADO' || ticket.estado === 'CANCELADO';

  return (
    <div
      onClick={() => onTap(ticket.id)}
      className={cn(
        'px-4 py-3 border-b border-border active:bg-muted/50 transition-colors cursor-pointer',
        isClosed && 'opacity-60'
      )}
    >
      {/* Row 1: Number + Title + Priority */}
      <div className="flex items-start gap-2">
        <span className="text-xs text-muted-foreground font-mono shrink-0">
          #{ticket.numero}
        </span>
        <span className={cn('text-sm font-medium line-clamp-2 flex-1', isClosed && 'line-through')}>
          {ticket.titulo}
        </span>
        {PRIORITY_LABELS[ticket.prioridad] && (
          <span className={cn('text-sm font-bold shrink-0', PRIORITY_COLORS[ticket.prioridad])}>
            {PRIORITY_LABELS[ticket.prioridad]}
          </span>
        )}
      </div>

      {/* Row 2: Client + Company */}
      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
        {ticket.cliente?.nombre && (
          <span className="truncate max-w-[140px]">{ticket.cliente.nombre}</span>
        )}
        {ticket.empresa?.nombre && (
          <>
            <span>·</span>
            <span className="truncate max-w-[120px]">{ticket.empresa.nombre}</span>
          </>
        )}
      </div>

      {/* Row 3: Status + Assignee + Time + Counters */}
      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
        <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full', STATUS_COLORS[ticket.estado] || 'bg-gray-100 text-gray-800')}>
          {STATUS_LABELS[ticket.estado] || ticket.estado}
        </span>

        {ticket.asignado_a?.nombre && (
          <span className="text-xs text-muted-foreground">
            @{ticket.asignado_a.nombre}
          </span>
        )}

        {ticket.created_at && (
          <span className="text-xs text-muted-foreground">
            {formatRelativeTime(ticket.created_at)}
          </span>
        )}

        {ticket.fecha_limite && (
          <span className="text-xs text-muted-foreground flex items-center gap-0.5">
            <Clock className="h-3 w-3" />
            {formatDate(ticket.fecha_limite)}
          </span>
        )}

        <div className="flex-1" />

        {(ticket.comentarios_count ?? 0) > 0 && (
          <span className="text-xs text-muted-foreground flex items-center gap-0.5">
            <MessageSquare className="h-3 w-3" />
            {ticket.comentarios_count}
          </span>
        )}
        {(ticket.adjuntos_count ?? 0) > 0 && (
          <span className="text-xs text-muted-foreground flex items-center gap-0.5">
            <Paperclip className="h-3 w-3" />
            {ticket.adjuntos_count}
          </span>
        )}
      </div>
    </div>
  );
}
